import React, {useMemo} from 'react';
import {shortenAddress} from '@usedapp/core';
import {useScopeTranslation} from '@uigstudio/uig-nextjs-helper';

import {Text} from '~atoms/Text';
import {PlotDetailsResponse} from '~types/Plots';
import {formatNumber, formatPrice} from '~utils/number';
import CopyToClipboardWithTooltip from '~components/atoms/CopyToClipboardWithTooltip';
import domeIconSrc from '~assets/images/icons/ui/domeIcon.png';
import Icon from '~atoms/Icon';

import {FlexCenter, InfoColumn, InfoRow, Label} from './styles';

type PlotInfoProps = {
    plot?: PlotDetailsResponse;
};

const PlotInfo: React.FC<PlotInfoProps> = ({plot}) => {
    const t = useScopeTranslation('map', 'preview');
    const {url, host} = useMemo(
        () => ({
            url: plot?.ownerUrl,
            host: plot?.ownerUrl ? new URL(plot.ownerUrl).host : undefined,
        }),
        [plot],
    );
    const {priceDome, priceUsd} = useMemo(
        () => ({
            priceDome: formatNumber(plot?.currentPrice?.dome as number),
            priceUsd: formatPrice(plot?.currentPrice?.usd as number, '\xa0$\xa0'),
        }),
        [plot],
    );

    const isSold = useMemo(() => Boolean(plot?.isSold), [plot]);
    return (
        <InfoRow>
            {plot?.currentPrice && (
                <InfoColumn active={isSold}>
                    <Label>{isSold ? t('soldFor') : t('startingPrice')}</Label>
                    <Text>
                        <FlexCenter>
                            <Icon src={domeIconSrc} width={16} height={16} />
                            {'\xa0\xa0'}
                            {priceDome}
                        </FlexCenter>
                    </Text>
                    <Text>{isSold ? priceUsd : t('notSold')}</Text>
                </InfoColumn>
            )}
            <InfoColumn active={true}>
                {(plot?.owner || url) && (
                    <>
                        <Label>{t('owner')}</Label>
                        <Text>
                            {plot?.owner?.walletAddress ? (
                                <>
                                    {shortenAddress(plot.owner.walletAddress)}
                                    <CopyToClipboardWithTooltip
                                        text={plot.owner.walletAddress}
                                        type="address"
                                    />
                                </>
                            ) : (
                                ''
                            )}
                        </Text>
                        {url && (
                            <Text as={'a'} href={url} target={'_blank'}>
                                {host}
                            </Text>
                        )}
                    </>
                )}
            </InfoColumn>
        </InfoRow>
    );
};

export default PlotInfo;
